import React, { useState, useMemo, useEffect, useRef } from "react";
import { List } from "@mui/material";
import { ConfirmDialog, ScrollArea } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import groupBy from "lodash/groupBy";
import {
  GET_SETTING_POLICIES,
  UPDATE_TRANSACTION_SETTING_POLICY_ENABLED_STATUS,
} from "../../../graphql/settingPolicy";
import SettingPolicyType from "../../../enums/SettingPolicyType";
import TransactionPoliciesDrawer from "./TransactionPoliciesDrawer";
import LoadingWithoutModal from "../../common/LoadingWithoutModal";
import { useTransactionPoliciesSetting } from "hooks/useTransactionPoliciesSetting";
import { usePolicyNavigationGuard } from "hooks/usePolicyNavigationGuard";
import { CategorySetting, TransactionPolicy } from "hooks/settingPolicy.types";
import SaveOrDiscardDialog from "components/common/SaveOrDiscardDialog";
import { Columns, Stack } from "components/layout/reusableLayouts";
import PoliciesToolbar from "./policies/PoliciesToolbar";
import RolePane from "./policies/RolePane";
import PolicyTypePane from "./policies/PolicyTypePane";
import CategorySettingRow from "./policies/CategorySettingRow";
import { PANE_PADDING_X, PANE_TRACKS } from "./policies/paneLayout";

/** What the page header needs to render this tab's Save / Discard / Bulk update. */
export type TransactionPolicyActions = {
  isDirty: boolean;
  bulkUpdateDisabled: boolean;
  onDiscard: () => void;
  /** Resolves false when the save failed, so a caller can stay put. */
  onSave: () => Promise<boolean>;
  onBulkUpdate: () => void;
};

/** Where the user asked to go inside this tab: a role, or a policy type. */
type PolicyDestination = {
  role?: string;
  policyType?: SettingPolicyType;
};

interface TransactionsPolicyTableProps {
  /** Called with null on unmount, so the header empties when the tab changes. */
  onActionsChange?: (actions: TransactionPolicyActions | null) => void;
}

const TransactionsPolicyTable: React.FC<TransactionsPolicyTableProps> = ({
  onActionsChange,
}) => {
  const { t } = useTranslation();

  const [userRole, setUserRole] = useState<string>("Employee");
  const [policyType, setPolicyType] = useState(
    SettingPolicyType.TRANSACTION_COST_ALLOCATION,
  );

  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedPolicy, setSelectedPolicy] =
    useState<TransactionPolicy | null>(null);
  const [updating, setUpdating] = useState(false);
  const [isShowConfirmDisablePolicy, setIsShowConfirmDisablePolicy] =
    useState(false);

  const {
    data: settingPoliciesData,
    refetch: refetchSettingPolicies,
    loading: isLoading,
  } = useQuery(GET_SETTING_POLICIES, {
    variables: {
      settingPolicyTypes: [
        SettingPolicyType.TRANSACTION_COST_ALLOCATION,
        SettingPolicyType.TRANSACTION_RECEIPT,
      ],
    },
  });

  const {
    addSetting,
    removeSetting,
    updatingSettings,
    handleUpdateAllCategories,
    discardUpdatedSettings,
    submitUpdatedSettings,
    setSelectedPolicySetting,
    handleUpdateCategoriesAmount,
    isUpdating,
  } = useTransactionPoliciesSetting({ refetchSettingPolicies });

  const [updateTransactionSettingPolicyEnabledStatus] = useMutation(
    UPDATE_TRANSACTION_SETTING_POLICY_ENABLED_STATUS,
    {
      onCompleted: () => {
        refetchSettingPolicies();
        toast.success(t("update-setting-policy-success"));
        setUpdating(false);
      },
      onError: (err) => {
        toast.error(err.message);
        setShowDrawer(false);
        setUpdating(false);
      },
    },
  );

  const policiesForRole: TransactionPolicy[] = useMemo(() => {
    const byUserRole = settingPoliciesData
      ? groupBy(settingPoliciesData.mySettingPolicies, "userRole")
      : {};
    return (
      byUserRole[`${userRole}`]?.sort((a: TransactionPolicy, b) =>
        a.type.localeCompare(b.type),
      ) || []
    );
  }, [userRole, settingPoliciesData]);

  const categories: CategorySetting[] = useMemo(() => {
    const selected = policiesForRole.find((item) => item.type === policyType);
    if (selected) {
      setSelectedPolicy(selected);
      setSelectedPolicySetting(selected);
    }
    return [...(selected?.policy?.categories || [])].sort((a, b) =>
      a.categoryName.localeCompare(b.categoryName),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, policiesForRole, policyType, settingPoliciesData]);

  const isCheckAllCategory = useMemo(() => {
    const pendingIds = updatingSettings.map((item) => item.categoryId);
    const checkedPending = updatingSettings.filter(
      (item) => item.isCategoryEnabled,
    );
    const untouchedAndOn = categories.filter(
      (item) => item.isCategoryEnabled && !pendingIds.includes(item.categoryId),
    );
    return checkedPending.length + untouchedAndOn.length === categories.length;
  }, [categories, updatingSettings]);

  const isDirty = updatingSettings.length > 0;
  const isPolicyEnabled = Boolean(selectedPolicy?.isPolicyTypeEnabled);

  const getPolicyTypeTitle = (type: SettingPolicyType) => {
    switch (type) {
      case SettingPolicyType.TRANSACTION_COST_ALLOCATION:
        return t("Allocation Policy");
      case SettingPolicyType.TRANSACTION_RECEIPT:
        return t("Receipt Policy");
    }
  };

  const handleEditPolicy = () => setShowDrawer(true);

  const handleUpdateEnabledStatusByType = (
    policy: TransactionPolicy | null,
  ) => {
    if (!policy) return;
    setUpdating(true);
    updateTransactionSettingPolicyEnabledStatus({
      variables: {
        updateEnabledStatusInput: {
          id: policy.id,
          status: !policy.isPolicyTypeEnabled,
        },
      },
    });
  };

  const guard = usePolicyNavigationGuard<PolicyDestination>({
    isDirty,
    onApply: ({ role, policyType: nextType }) => {
      if (role !== undefined) setUserRole(role);
      if (nextType !== undefined) setPolicyType(nextType);
    },
    onSave: submitUpdatedSettings,
    onDiscard: discardUpdatedSettings,
  });

  // Selecting the policy first keeps the confirm dialog pointed at the row the
  // user actually flipped, rather than at whatever was selected before.
  const handleTogglePolicyEnabled = (policy: TransactionPolicy) => {
    if (isDirty) {
      guard.request({ policyType: policy.type });
      return;
    }
    setPolicyType(policy.type);
    if (policy.isPolicyTypeEnabled) {
      setIsShowConfirmDisablePolicy(true);
    } else {
      handleUpdateEnabledStatusByType(policy);
    }
  };

  const onUpdateCategoriesAmount = (
    isAllCategories: boolean,
    amount: string,
  ) => {
    const selectedCategories = isAllCategories
      ? categories
      : categories.filter((item) => item.isCategoryEnabled);
    handleUpdateCategoriesAmount(selectedCategories, amount);
    setShowDrawer(false);
  };

  const onUpdateCategory = (
    item: CategorySetting,
    updated: { amount: string; isCategoryEnabled: boolean },
  ) => {
    if (
      `${item.amount}` !== updated.amount ||
      // The stored flag is null on a category nobody has configured, and the
      // form always sends a boolean, so a raw !== reports a change that undoing
      // the toggle can never take back.
      Boolean(item.isCategoryEnabled) !== updated.isCategoryEnabled
    ) {
      addSetting({ ...item, ...updated });
    } else {
      removeSetting(item);
    }
  };

  const handleToggleSelectAllCategories = () => {
    const toFlip = categories.filter((item) =>
      isCheckAllCategory ? item.isCategoryEnabled : !item.isCategoryEnabled,
    );
    handleUpdateAllCategories(toFlip, !isCheckAllCategory);
  };

  // The header owns the buttons but not the state, so the handlers go up
  // through a ref: keyed on the booleans alone they would close over a stale
  // updatingSettings and save the wrong set.
  const handlersRef = useRef({
    onDiscard: discardUpdatedSettings,
    onSave: submitUpdatedSettings,
    onBulkUpdate: handleEditPolicy,
  });
  useEffect(() => {
    handlersRef.current = {
      onDiscard: discardUpdatedSettings,
      onSave: submitUpdatedSettings,
      onBulkUpdate: handleEditPolicy,
    };
  });

  const stableHandlers = useRef({
    onDiscard: () => handlersRef.current.onDiscard(),
    onSave: () => handlersRef.current.onSave(),
    onBulkUpdate: () => handlersRef.current.onBulkUpdate(),
  }).current;

  useEffect(() => {
    onActionsChange?.({
      isDirty,
      bulkUpdateDisabled: !isPolicyEnabled,
      ...stableHandlers,
    });
  }, [isDirty, isPolicyEnabled, onActionsChange, stableHandlers]);

  useEffect(() => () => onActionsChange?.(null), [onActionsChange]);

  return (
    <Stack gap="0" sx={{ height: "100%", minHeight: 0, width: "100%" }}>
      <PoliciesToolbar
        isAllSelected={isCheckAllCategory}
        disabled={!isPolicyEnabled}
        onToggleAll={handleToggleSelectAllCategories}
      />

      <ScrollArea sx={{ flex: 1, minHeight: 0 }}>
        <Columns count={PANE_TRACKS} gap="0">
          <RolePane
            selectedRole={userRole}
            onSelectRole={(role) => guard.request({ role })}
          />

          <PolicyTypePane
            policies={policiesForRole}
            selectedType={policyType}
            getTitle={getPolicyTypeTitle}
            onSelectType={(policyType) => guard.request({ policyType })}
            onToggleEnabled={handleTogglePolicyEnabled}
          />

          <List sx={{ padding: PANE_PADDING_X }}>
            {categories.map((item) => (
              <CategorySettingRow
                key={`${userRole}-${policyType}-${item.categoryId}`}
                policy={selectedPolicy}
                item={item}
                updatingCategories={updatingSettings}
                onUpdateCategory={onUpdateCategory}
              />
            ))}
          </List>
        </Columns>
      </ScrollArea>

      <TransactionPoliciesDrawer
        open={showDrawer}
        handleCloseDrawer={() => setShowDrawer(false)}
        policy={selectedPolicy}
        onUpdateCategoriesAmount={onUpdateCategoriesAmount}
        isAllCategories={isCheckAllCategory}
      />
      <ConfirmDialog
        open={isShowConfirmDisablePolicy}
        tone="primary"
        title={t("settingPolicies.disablePolicyTitle")}
        message={t("settingPolicies.disablePolicyDescription")}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        onConfirm={() => {
          handleUpdateEnabledStatusByType(selectedPolicy);
          setIsShowConfirmDisablePolicy(false);
        }}
        onCancel={() => setIsShowConfirmDisablePolicy(false)}
      />
      <SaveOrDiscardDialog
        open={guard.isPrompting}
        title={t("settingPolicies.unsavedChangesTitle")}
        message={t("settingPolicies.unsavedChangesDescription")}
        saveLabel={t("Save")}
        discardLabel={t("settingPolicies.discard")}
        onSave={guard.save}
        onDiscard={guard.discard}
        onCancel={guard.cancel}
      />
      <LoadingWithoutModal open={isLoading || updating || isUpdating} />
    </Stack>
  );
};

export default TransactionsPolicyTable;
